import { useEffect } from "react";
import {
  enhanceMatrixRadioTooltips,
  removeMatrixRadioTooltips,
  closeOpenMatrixTooltips,
  toggleTouchMatrixTooltip,
  getMatrixTooltipTriggerFromEvent,
} from "../utils/surveyMatrixTooltips";

export const useMatrixSurveyTooltips = (surveyModel) => {
  useEffect(() => {
    if (!surveyModel) return;

    let animationFrameId = null;
    let observedSurveyRoot = null;
    let resizeObserver = null;
    let mutationObserver = null;

    const pendingTimeouts = new Set();

    const clearPendingTimeouts = () => {
      pendingTimeouts.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });

      pendingTimeouts.clear();
    };

    const applyTooltips = () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = null;

        const surveyRoot = document.querySelector(".sd-root-modern");
        if (!surveyRoot) return;

        enhanceMatrixRadioTooltips(surveyRoot);
      });
    };

    const scheduleResponsiveRefresh = () => {
      clearPendingTimeouts();

      applyTooltips();

      [100, 250, 500].forEach((delay) => {
        const timeoutId = window.setTimeout(() => {
          pendingTimeouts.delete(timeoutId);
          applyTooltips();
        }, delay);

        pendingTimeouts.add(timeoutId);
      });
    };

    const matrixTableWasAdded = (mutation) => {
      if (mutation.type !== "childList") return false;

      return Array.from(mutation.addedNodes).some((node) => {
        if (!(node instanceof Element)) return false;

        return node.matches(".sd-table.sd-matrix__table") || Boolean(node.querySelector(".sd-table.sd-matrix__table"));
      });
    };

    const matrixTableClassChanged = (mutation) => {
      if (mutation.type !== "attributes") return false;
      if (mutation.attributeName !== "class") return false;
      if (!(mutation.target instanceof Element)) return false;

      return mutation.target.matches(".sd-table.sd-matrix__table");
    };

    const observeSurveyRoot = () => {
      const surveyRoot = document.querySelector(".sd-root-modern");
      if (!surveyRoot) return;

      if (observedSurveyRoot === surveyRoot) return;

      resizeObserver?.disconnect();
      mutationObserver?.disconnect();

      observedSurveyRoot = surveyRoot;

      resizeObserver = new ResizeObserver(() => {
        scheduleResponsiveRefresh();
      });

      resizeObserver.observe(surveyRoot);

      mutationObserver = new MutationObserver((mutations) => {
        const matrixLayoutChanged = mutations.some((mutation) => matrixTableClassChanged(mutation) || matrixTableWasAdded(mutation));

        if (matrixLayoutChanged) {
          applyTooltips();
        }
      });

      mutationObserver.observe(surveyRoot, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ["class"],
      });
    };

    const handleSurveyRender = () => {
      observeSurveyRoot();
      applyTooltips();
    };

    const handleResponsiveChange = () => {
      observeSurveyRoot();
      scheduleResponsiveRefresh();
    };

    handleSurveyRender();

    surveyModel.onAfterRenderQuestion.add(handleSurveyRender);
    surveyModel.onValueChanged.add(handleSurveyRender);
    surveyModel.onCurrentPageChanged.add(handleSurveyRender);

    window.addEventListener("resize", handleResponsiveChange);
    window.addEventListener("orientationchange", handleResponsiveChange);

    window.visualViewport?.addEventListener("resize", handleResponsiveChange);

    return () => {
      surveyModel.onAfterRenderQuestion.remove(handleSurveyRender);
      surveyModel.onValueChanged.remove(handleSurveyRender);
      surveyModel.onCurrentPageChanged.remove(handleSurveyRender);

      window.removeEventListener("resize", handleResponsiveChange);
      window.removeEventListener("orientationchange", handleResponsiveChange);

      window.visualViewport?.removeEventListener("resize", handleResponsiveChange);

      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }

      clearPendingTimeouts();

      resizeObserver?.disconnect();
      mutationObserver?.disconnect();

      const surveyRoot = document.querySelector(".sd-root-modern");
      removeMatrixRadioTooltips(surveyRoot);
    };
  }, [surveyModel]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      const trigger = getMatrixTooltipTriggerFromEvent(event);

      closeOpenMatrixTooltips(trigger);

      if (!trigger) return;

      const isTouchDevice = window.matchMedia("(hover: none)").matches || window.matchMedia("(pointer: coarse)").matches;

      if (isTouchDevice) {
        event.stopPropagation();
        toggleTouchMatrixTooltip(trigger);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, []);
};
